// BankLivePanel.js — "Start today" mode within BankMarketSimulatorPage.
// Shows forward book blotter, P&L lines, daily P&L curve, what-if overlay.

import React, { useState, useCallback, useEffect } from 'react';
import api from '../../services/api';
import { RefreshCw, XCircle, TrendingUp, AlertTriangle } from 'lucide-react';

const STATUS_LABELS = {
  active: 'Active',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

function formatCurrency(value) {
  if (value == null) return '-';
  return '$' + Math.abs(parseFloat(value)).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export default function BankLivePanel() {
  const [bankRuns, setBankRuns] = useState([]);
  const [activeRun, setActiveRun] = useState(null);
  const [runDetail, setRunDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [whatIfBps, setWhatIfBps] = useState('');
  const [whatIfResult, setWhatIfResult] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Create form state
  const [createParams, setCreateParams] = useState({
    forwardPricingBps: 50,
    hedgeEnabled: true,
    marketSharePct: 5,
    exposureLimit: 10000000,
    endDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
  });

  const loadRuns = useCallback(async () => {
    try {
      const response = await api.getBankSimRuns();
      if (response.success) {
        const liveRuns = response.data.filter(r => r.mode === 'live');
        setBankRuns(liveRuns);
        if (liveRuns.length > 0 && !activeRun) {
          setActiveRun(liveRuns[0]);
          loadRunDetail(liveRuns[0].id);
        }
      }
    } catch (err) {
      console.warn('Failed to load bank runs:', err.message);
    }
  }, [activeRun]);

  const loadRunDetail = useCallback(async (runId) => {
    setLoading(true);
    try {
      const response = await api.getBankSimRun(runId);
      if (response.success) {
        setRunDetail(response.data);
        setActiveRun(response.data.run);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCreate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/simulator/bank/live', createParams);
      if (response.success) {
        setShowCreateForm(false);
        loadRuns();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [createParams, loadRuns]);

  const handleWhatIf = useCallback(async () => {
    if (!activeRun || !whatIfBps) return;
    try {
      const response = await api.post(`/simulator/bank/runs/${activeRun.id}/what-if`, {
        altForwardBps: parseFloat(whatIfBps),
      });
      if (response.success) {
        setWhatIfResult(response.data);
      }
    } catch (err) {
      setError(err.message);
    }
  }, [activeRun, whatIfBps]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  const run = activeRun;
  const positions = runDetail?.positions || [];
  const plLines = runDetail?.plLines || [];

  return (
    <div className="space-y-4">
      {/* Live badge */}
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-600 text-white">
          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
          LIVE BANK SIMULATION
        </span>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 rounded-lg px-4 py-3 text-sm">{error}</div>
      )}

      {/* No active run */}
      {!run && !loading && !showCreateForm && (
        <div className="text-center py-12">
          <TrendingUp className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p className="text-sm text-gray-500 mb-4">
            Start a live bank simulation to track P&L as real data arrives.
          </p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800"
          >
            Create Live Bank Run
          </button>
        </div>
      )}

      {/* Create form */}
      {showCreateForm && (
        <div className="bg-gray-50 rounded-xl p-5 space-y-4">
          <h3 className="text-sm font-semibold text-gray-900">New Live Bank Run</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Forward Pricing (bps)</label>
              <input
                type="number" min="0" max="500" step="5"
                value={createParams.forwardPricingBps}
                onChange={e => setCreateParams(p => ({ ...p, forwardPricingBps: parseFloat(e.target.value) }))}
                className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Market Share (%)</label>
              <input
                type="number" min="0.1" max="100" step="0.5"
                value={createParams.marketSharePct}
                onChange={e => setCreateParams(p => ({ ...p, marketSharePct: parseFloat(e.target.value) }))}
                className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Exposure Limit ($)</label>
              <input
                type="number" min="0" step="100000"
                value={createParams.exposureLimit}
                onChange={e => setCreateParams(p => ({ ...p, exposureLimit: parseFloat(e.target.value) }))}
                className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">End Date</label>
              <input
                type="date"
                value={createParams.endDate}
                onChange={e => setCreateParams(p => ({ ...p, endDate: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg text-sm"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox" checked={createParams.hedgeEnabled}
              onChange={e => setCreateParams(p => ({ ...p, hedgeEnabled: e.target.checked }))}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">Hedge forward book</span>
          </label>
          <div className="flex gap-2">
            <button onClick={handleCreate} disabled={loading}
              className="px-4 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-800 disabled:opacity-50">
              {loading ? 'Creating...' : 'Start Run'}
            </button>
            <button onClick={() => setShowCreateForm(false)}
              className="px-4 py-2 text-gray-600 text-sm rounded-lg hover:bg-gray-100">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Active run detail */}
      {run && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className={`rounded-lg p-3 ${parseFloat(run.net_revenue || 0) >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
              <p className="text-[10px] text-gray-500 uppercase">Net P&L</p>
              <p className={`text-sm font-semibold font-mono ${parseFloat(run.net_revenue || 0) >= 0 ? 'text-emerald-800' : 'text-red-800'}`}>
                {formatCurrency(run.net_revenue)}
              </p>
            </div>
            <div className="rounded-lg p-3 bg-gray-50">
              <p className="text-[10px] text-gray-500 uppercase">Forward Book</p>
              <p className="text-sm font-semibold font-mono text-gray-800">
                {positions.filter(p => p.forward_written).length} positions
              </p>
            </div>
            <div className="rounded-lg p-3 bg-gray-50">
              <p className="text-[10px] text-gray-500 uppercase">Break-even</p>
              <p className="text-sm font-semibold font-mono text-gray-800">
                {run.break_even_forward_bps ? `${parseFloat(run.break_even_forward_bps).toFixed(1)} bps` : '-'}
              </p>
            </div>
            <div className="rounded-lg p-3 bg-gray-50">
              <p className="text-[10px] text-gray-500 uppercase">Hedge</p>
              <p className="text-sm font-semibold text-gray-800">
                {run.hedge_enabled ? 'On' : 'Off'}
              </p>
            </div>
          </div>

          {/* Forward book blotter */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Forward Book
            </h3>
            <div className="overflow-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-2 text-gray-500">Entry</th>
                    <th className="text-right py-2 px-2 text-gray-500">Notional</th>
                    <th className="text-right py-2 px-2 text-gray-500">Fwd Rate</th>
                    <th className="text-right py-2 px-2 text-gray-500">Fwd bps</th>
                    <th className="text-right py-2 px-2 text-gray-500">Hedge Rate</th>
                    <th className="text-right py-2 px-2 text-gray-500">Spread</th>
                  </tr>
                </thead>
                <tbody>
                  {positions.filter(p => p.forward_written).length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-6 text-gray-400">No forward positions yet</td></tr>
                  ) : (
                    positions.filter(p => p.forward_written).map((pos, idx) => (
                      <tr key={idx} className="border-b border-gray-50">
                        <td className="py-1.5 px-2 font-mono">{pos.entry_date}</td>
                        <td className="py-1.5 px-2 text-right font-mono">{formatCurrency(pos.notional_base)}</td>
                        <td className="py-1.5 px-2 text-right font-mono">
                          {pos.forward_rate ? parseFloat(pos.forward_rate).toFixed(2) : '-'}
                        </td>
                        <td className="py-1.5 px-2 text-right font-mono">{pos.forward_pricing_bps_at_entry}</td>
                        <td className="py-1.5 px-2 text-right font-mono">
                          {pos.hedge_rate ? parseFloat(pos.hedge_rate).toFixed(2) : '-'}
                        </td>
                        <td className="py-1.5 px-2 text-right font-mono">
                          {pos.forward_premium_income ? `$${parseFloat(pos.forward_premium_income).toFixed(0)}` : '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* What-if overlay */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              What-if Analysis
            </h3>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="block text-xs text-gray-600 mb-1">Alternative Forward Price (bps)</label>
                <input
                  type="number" min="0" max="500" step="5"
                  value={whatIfBps}
                  onChange={e => setWhatIfBps(e.target.value)}
                  placeholder="e.g. 80"
                  className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
                />
              </div>
              <button
                onClick={handleWhatIf}
                disabled={!whatIfBps}
                className="px-4 py-2 bg-gray-800 text-white text-sm rounded-lg hover:bg-gray-700 disabled:opacity-50"
              >
                Compare
              </button>
            </div>
            {whatIfResult && (
              <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                <div className="bg-white rounded p-2">
                  <p className="text-gray-500">Original ({whatIfResult.original_forward_bps} bps)</p>
                  <p className="font-mono font-medium">{formatCurrency(whatIfResult.original_net_pl)}</p>
                </div>
                <div className="bg-white rounded p-2">
                  <p className="text-gray-500">What-if ({whatIfResult.alternative_forward_bps} bps)</p>
                  <p className="font-mono font-medium">{formatCurrency(whatIfResult.what_if_net_pl)}</p>
                </div>
                <div className={`rounded p-2 ${whatIfResult.difference >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                  <p className="text-gray-500">Difference</p>
                  <p className={`font-mono font-medium ${whatIfResult.difference >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    {whatIfResult.difference >= 0 ? '+' : ''}{formatCurrency(whatIfResult.difference)}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
            <button
              onClick={() => loadRunDetail(run.id)}
              className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Refresh
            </button>
          </div>
        </>
      )}

      {/* Run list */}
      {bankRuns.length > 1 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            Your Bank Runs
          </h3>
          <div className="space-y-2">
            {bankRuns.map(br => (
              <button
                key={br.id}
                onClick={() => { setActiveRun(br); loadRunDetail(br.id); }}
                className={`w-full text-left p-3 rounded-lg border transition-colors text-xs ${
                  run?.id === br.id ? 'border-blue-300 bg-blue-50' : 'border-gray-100 hover:bg-gray-50'
                }`}
              >
                <span className="font-medium">{br.destination_country}</span>
                <span className="text-gray-400 ml-2">{STATUS_LABELS[br.status] || br.status}</span>
                <span className="text-gray-400 ml-2 font-mono">{br.market_share_pct}% share</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
